import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, Building2, ChevronDown, ChevronRight, Cloud, CloudOff,
  Download, FileArchive, FileText, Mail, PlayCircle, RefreshCw, Search, UserCircle2,
} from 'lucide-react';
import { ADMIN_PALETTE } from '../adminDesignSystem';
import { API, formatEur, formatMonth, MyDataBadge, StatCard } from './complianceShared';

function InvoiceRow({ inv, headers, onRefresh }) {
  const [downloading, setDownloading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/invoices/${inv.id}/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `allure-${inv.kind}-${inv.number || inv.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
    finally { setDownloading(false); }
  };

  const resend = async () => {
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/resend`, {}, { headers });
      toast.success('Email resent');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resend failed'); }
  };

  const submitMyData = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/invoices/${inv.id}/submit-mydata`, {}, { headers });
      if (res.data.ok) toast.success(`Submitted to myDATA — MARK ${res.data.mark || '—'}`);
      else toast.error(`myDATA rejected: ${(res.data.errors || []).join(', ')}`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'myDATA submission failed'); }
    finally { setSubmitting(false); }
  };

  const canRetryMydata = inv.kind === 'venue' && ['pending', 'rejected', 'error'].includes(inv.mydata_status);

  return (
    <li
      key={inv.id}
      data-testid={`admin-invoice-row-${inv.id}`}
      style={{ padding: '10px 12px', borderRadius: 12, background: ADMIN_PALETTE.surface, border: `1px solid ${ADMIN_PALETTE.border}` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14, color: ADMIN_PALETTE.ink }}>{inv.number}</strong>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: inv.kind === 'venue' ? '#EEF2FF' : '#DCFCE7', color: inv.kind === 'venue' ? '#3730A3' : '#166534', fontWeight: 700 }}>
            {inv.kind === 'venue' ? 'VAT invoice' : 'Host statement'}
          </span>
          {inv.email_status && (
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: inv.email_status === 'sent' ? '#DCFCE7' : inv.email_status === 'failed' ? '#FEE2E2' : '#FEF3C7', color: inv.email_status === 'sent' ? '#166534' : inv.email_status === 'failed' ? '#991B1B' : '#92400E', fontWeight: 600 }}>
              email {inv.email_status}
            </span>
          )}
          <MyDataBadge status={inv.mydata_status} mark={inv.mydata_mark} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {canRetryMydata && (
            <button type="button" onClick={submitMyData} disabled={submitting} data-testid={`admin-invoice-mydata-${inv.id}`}
              style={{ border: '1px solid #7C3AED', background: '#7C3AED', color: '#fff', padding: '5px 10px', borderRadius: 8, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700, opacity: submitting ? 0.6 : 1 }}>
              {submitting ? '…' : 'Submit myDATA'}
            </button>
          )}
          <button type="button" onClick={download} disabled={downloading} data-testid={`admin-invoice-pdf-${inv.id}`}
            style={{ border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface, padding: '5px 10px', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <Download size={12} /> PDF
          </button>
          <button type="button" onClick={resend} disabled={!inv.recipient_email} data-testid={`admin-invoice-resend-${inv.id}`}
            style={{ border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface, padding: '5px 10px', borderRadius: 8, cursor: inv.recipient_email ? 'pointer' : 'not-allowed', opacity: inv.recipient_email ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <Mail size={12} /> Resend
          </button>
        </div>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 12, color: ADMIN_PALETTE.inkMuted }}>
        {inv.buyer_snapshot?.name || '—'} · {formatMonth(inv.month_key)} · {inv.kind === 'venue' ? formatEur(inv.total_gross) : formatEur(inv.total_credited)}
        {inv.mydata_error ? <span style={{ color: '#991B1B', marginLeft: 8 }}>{inv.mydata_error}</span> : null}
      </p>
    </li>
  );
}

export function InvoicesTab({ headers }) {
  const [items, setItems] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterKind, setFilterKind] = useState('');
  const [manualMonth, setManualMonth] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [viewMode, setViewMode] = useState('venue');
  const [expandedVenues, setExpandedVenues] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterMonth) params.month_key = filterMonth;
      if (filterKind) params.kind = filterKind;
      if (searchQ) params.q = searchQ;
      const res = await axios.get(`${API}/admin/invoices`, { headers, params });
      setItems(res.data.items || []);
      setMonths(res.data.months || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invoices');
    } finally { setLoading(false); }
  }, [headers, filterMonth, filterKind, searchQ]);

  useEffect(() => { load(); }, [load]);

  const runManual = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/invoices/run`, { month_key: manualMonth || null, send_emails: true }, { headers });
      const { venues, hosts } = res.data;
      toast.success(`Run complete — Venues: ${venues.created} created (${venues.mydata_submitted || 0} myDATA OK, ${venues.mydata_failed || 0} failed) · Hosts: ${hosts.created} created`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
    finally { setRunning(false); }
  };

  const downloadZip = async () => {
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.set('month_key', filterMonth);
      if (filterKind) params.set('kind', filterKind);
      const res = await axios.get(`${API}/admin/invoices/export.zip?${params.toString()}`, { headers, responseType: 'blob' });
      const bloburl = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a'); a.href = bloburl;
      a.download = `allure-invoices-${filterMonth || filterKind || 'all'}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(bloburl);
    } catch (e) { toast.error(e.response?.data?.detail || 'ZIP export failed'); }
  };

  const venueGroups = useMemo(() => {
    const venueInvoices = items.filter(i => i.kind === 'venue');
    const groups = {};
    for (const inv of venueInvoices) {
      const key = inv.buyer_snapshot?.id || inv.buyer_snapshot?.name || 'Unknown';
      const name = inv.buyer_snapshot?.name || 'Unknown venue';
      if (!groups[key]) groups[key] = { name, invoices: [] };
      groups[key].invoices.push(inv);
    }
    return Object.entries(groups).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [items]);

  const toggleVenue = (key) => setExpandedVenues(prev => ({ ...prev, [key]: !prev[key] }));

  const myDataStats = useMemo(() => {
    const venue = items.filter(i => i.kind === 'venue');
    return {
      submitted: venue.filter(i => i.mydata_status === 'submitted').length,
      pending: venue.filter(i => i.mydata_status === 'pending').length,
      failed: venue.filter(i => ['rejected', 'error'].includes(i.mydata_status)).length,
    };
  }, [items]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {items.some(i => i.kind === 'venue') && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <StatCard icon={Cloud} label="myDATA submitted" value={myDataStats.submitted} tone="ok" testid="mydata-stat-submitted" />
          <StatCard icon={AlertTriangle} label="myDATA pending" value={myDataStats.pending} tone={myDataStats.pending ? 'warn' : 'ok'} testid="mydata-stat-pending" />
          <StatCard icon={CloudOff} label="myDATA failed" value={myDataStats.failed} tone={myDataStats.failed ? 'danger' : 'ok'} testid="mydata-stat-failed" />
        </div>
      )}

      <div style={{ padding: 14, borderRadius: 14, border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="text" placeholder="Manual month YYYY-MM (empty = previous month)" value={manualMonth} onChange={e => setManualMonth(e.target.value)}
          data-testid="admin-invoices-manual-month"
          style={{ flex: '1 1 240px', padding: '10px 12px', borderRadius: 10, border: `1px solid ${ADMIN_PALETTE.border}`, fontSize: 14, fontFamily: 'inherit', background: ADMIN_PALETTE.surfaceMuted, outline: 'none' }} />
        <button type="button" onClick={runManual} disabled={running} data-testid="admin-invoices-run"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: 'none', background: ADMIN_PALETTE.ink, color: '#fff', fontWeight: 700, fontSize: 13, cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.6 : 1 }}>
          <PlayCircle size={16} /> {running ? 'Running…' : 'Run generation'}
        </button>
        <button type="button" onClick={downloadZip} data-testid="admin-invoices-zip"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          <FileArchive size={16} /> Download ZIP
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: ADMIN_PALETTE.inkMuted }} />
          <input type="text" placeholder="Search by invoice # or email…" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            data-testid="admin-invoices-search"
            style={{ width: '100%', paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8, borderRadius: 8, border: `1px solid ${ADMIN_PALETTE.border}`, fontSize: 13, fontFamily: 'inherit', background: ADMIN_PALETTE.surface, boxSizing: 'border-box' }} />
        </div>
        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} data-testid="admin-invoices-filter-month"
          style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${ADMIN_PALETTE.border}`, fontSize: 13, fontFamily: 'inherit', background: ADMIN_PALETTE.surface }}>
          <option value="">All months</option>
          {months.map(m => <option key={m} value={m}>{formatMonth(m)}</option>)}
        </select>
        <select value={filterKind} onChange={e => setFilterKind(e.target.value)} data-testid="admin-invoices-filter-kind"
          style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${ADMIN_PALETTE.border}`, fontSize: 13, fontFamily: 'inherit', background: ADMIN_PALETTE.surface }}>
          <option value="">All kinds</option>
          <option value="venue">Venue invoices</option>
          <option value="host_statement">Host statements</option>
        </select>
        <button type="button" onClick={load} data-testid="admin-invoices-refresh"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface, fontSize: 13, cursor: 'pointer' }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        {[{ id: 'venue', label: 'By Venue', icon: Building2 }, { id: 'all', label: 'All Invoices', icon: FileText }].map(v => {
          const Icon = v.icon;
          const active = viewMode === v.id;
          return (
            <button key={v.id} type="button" onClick={() => setViewMode(v.id)} data-testid={`invoices-view-${v.id}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 999, border: `1px solid ${active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`, background: active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.surface, color: active ? '#fff' : ADMIN_PALETTE.ink, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <Icon size={13} /> {v.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>Loading…</p>
      ) : items.length === 0 ? (
        <div data-testid="admin-invoices-empty" style={{ padding: 24, borderRadius: 12, border: `1px dashed ${ADMIN_PALETTE.border}`, textAlign: 'center', color: ADMIN_PALETTE.inkMuted, fontSize: 14 }}>
          No invoices match the filters.
        </div>
      ) : viewMode === 'venue' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {venueGroups.map(([key, group]) => {
            const open = expandedVenues[key];
            const myDataOk = group.invoices.filter(i => i.mydata_status === 'submitted').length;
            const myDataFail = group.invoices.filter(i => ['rejected','error'].includes(i.mydata_status)).length;
            return (
              <div key={key} data-testid={`venue-folder-${key}`} style={{ borderRadius: 14, border: `1px solid ${ADMIN_PALETTE.border}`, overflow: 'hidden' }}>
                <button type="button" onClick={() => toggleVenue(key)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: ADMIN_PALETTE.surfaceMuted, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  {open ? <ChevronDown size={16} color={ADMIN_PALETTE.inkMuted} /> : <ChevronRight size={16} color={ADMIN_PALETTE.inkMuted} />}
                  <Building2 size={15} color={ADMIN_PALETTE.inkMuted} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: ADMIN_PALETTE.ink, flex: 1 }}>{group.name}</span>
                  <span style={{ fontSize: 12, color: ADMIN_PALETTE.inkMuted }}>{group.invoices.length} invoice{group.invoices.length !== 1 ? 's' : ''}</span>
                  {myDataOk > 0 && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, background: '#DCFCE7', color: '#166534', fontWeight: 700 }}>{myDataOk} submitted</span>}
                  {myDataFail > 0 && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, background: '#FEE2E2', color: '#991B1B', fontWeight: 700 }}>{myDataFail} failed</span>}
                </button>
                {open && (
                  <ul style={{ listStyle: 'none', padding: '10px', margin: 0, display: 'flex', flexDirection: 'column', gap: 8, background: ADMIN_PALETTE.surface }}>
                    {group.invoices.map(inv => <InvoiceRow key={inv.id} inv={inv} headers={headers} onRefresh={load} />)}
                  </ul>
                )}
              </div>
            );
          })}
          {items.filter(i => i.kind === 'host_statement').length > 0 && (
            <div data-testid="host-statements-folder" style={{ borderRadius: 14, border: `1px solid ${ADMIN_PALETTE.border}`, overflow: 'hidden' }}>
              <button type="button" onClick={() => toggleVenue('__host_statements')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: ADMIN_PALETTE.surfaceMuted, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                {expandedVenues['__host_statements'] ? <ChevronDown size={16} color={ADMIN_PALETTE.inkMuted} /> : <ChevronRight size={16} color={ADMIN_PALETTE.inkMuted} />}
                <UserCircle2 size={15} color={ADMIN_PALETTE.inkMuted} />
                <span style={{ fontSize: 14, fontWeight: 700, color: ADMIN_PALETTE.ink, flex: 1 }}>Host Earnings Statements</span>
                <span style={{ fontSize: 12, color: ADMIN_PALETTE.inkMuted }}>{items.filter(i => i.kind === 'host_statement').length} statements</span>
              </button>
              {expandedVenues['__host_statements'] && (
                <ul style={{ listStyle: 'none', padding: '10px', margin: 0, display: 'flex', flexDirection: 'column', gap: 8, background: ADMIN_PALETTE.surface }}>
                  {items.filter(i => i.kind === 'host_statement').map(inv => <InvoiceRow key={inv.id} inv={inv} headers={headers} onRefresh={load} />)}
                </ul>
              )}
            </div>
          )}
        </div>
      ) : (
        <ul data-testid="admin-invoices-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(inv => <InvoiceRow key={inv.id} inv={inv} headers={headers} onRefresh={load} />)}
        </ul>
      )}
    </div>
  );
}
